import { useState, useEffect, useId, useCallback } from 'react'
import { X, PackageCheck, AlertCircle, Info } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { shouldCloseDialog, useEscapeClose } from '@/hooks/useEscapeClose'
import { formatVnNumber } from './poWorkflow'
import type { PoStatus } from '@/services/procurement'
import { PoStatusBadge } from '@/features/proto/ProtoBadges'
import { splitSpecColor } from './specColor'

export interface QuickReceiveLineItem {
  id: string
  itemName: string
  itemCode: string
  spec?: string
  spec_attributes?: Record<string, unknown> | null
  materialId?: string
  qty: number
  receivedQty: number
  remainingQty: number
  uom: string
}

export interface PoQuickReceiveModalProps {
  isOpen: boolean
  onClose: () => void
  poCode: string
  poStatus: PoStatus
  poKind?: 'STANDARD' | 'FREE'
  targetCol: 'PARTIAL' | 'COMPLETED'
  lines: QuickReceiveLineItem[]
  onConfirm: (
    receivedLines: Array<{ po_line_id: string; po_free_line_id?: string; qty: number }>,
    note?: string,
  ) => Promise<void>
}

export function PoQuickReceiveModal({
  isOpen,
  onClose,
  poCode,
  poStatus,
  poKind = 'STANDARD',
  targetCol,
  lines,
  onConfirm,
}: PoQuickReceiveModalProps) {
  const [inputQtys, setInputQtys] = useState<Record<string, string>>({})
  const [note, setNote] = useState('')
  const [errorInfo, setErrorInfo] = useState<{ message: string; materialId?: string } | null>(null)
  const setError = (msg: string | null) => setErrorInfo(msg ? { message: msg } : null)
  const [submitting, setSubmitting] = useState(false)
  const [initialInputQtys, setInitialInputQtys] = useState<Record<string, string>>({})
  const [initialNote, setInitialNote] = useState('')
  const noteId = useId()

  const isDraftGroup =
    poStatus === 'DRAFT' || poStatus === 'PENDING_APPROVAL' || poStatus === 'APPROVED'

  useEffect(() => {
    if (!isOpen) return

    // Pre-fill default inputs based on target column
    const initial: Record<string, string> = {}
    for (const line of lines) {
      if (targetCol === 'COMPLETED' || poKind === 'FREE') {
        initial[line.id] = String(line.remainingQty)
      } else {
        initial[line.id] = ''
      }
    }
    setInputQtys(initial)
    const nextNote = targetCol === 'COMPLETED' || poKind === 'FREE'
        ? 'Kanban nhận đủ hoàn thành đơn'
        : 'Kanban nhận hàng một phần'
    setInitialInputQtys(initial)
    setInitialNote(nextNote)
    setNote(nextNote)
    setError(null)
    setSubmitting(false)
  }, [isOpen, targetCol, lines])

  const hasUnsavedChanges = Object.keys(initialInputQtys).some((id) => inputQtys[id] !== initialInputQtys[id]) || note !== initialNote
  const requestClose = useCallback(() => {
    if (submitting || !shouldCloseDialog(hasUnsavedChanges)) return
    onClose()
  }, [hasUnsavedChanges, onClose, submitting])
  useEscapeClose({ enabled: isOpen, hasUnsavedChanges, onClose: requestClose })

  if (!isOpen) return null

  function handleFillAll() {
    const filled: Record<string, string> = {}
    for (const l of lines) {
      filled[l.id] = String(l.remainingQty)
    }
    setInputQtys(filled)
  }

  function handleResetAll() {
    const empty: Record<string, string> = {}
    for (const l of lines) {
      empty[l.id] = ''
    }
    setInputQtys(empty)
  }

  function handleLineQtyChange(lineId: string, val: string) {
    setInputQtys((prev) => ({ ...prev, [lineId]: val }))
  }

  function handleFillLine(lineId: string, maxQty: number) {
    setInputQtys((prev) => ({ ...prev, [lineId]: String(maxQty) }))
  }

  async function handleConfirm() {
    setError(null)

    const payloadLines: Array<{ po_line_id: string; po_free_line_id: string; qty: number }> = []

    for (const line of lines) {
      const rawVal = (inputQtys[line.id] ?? '').trim()
      if (!rawVal) continue

      const numVal = Number(rawVal)
      if (isNaN(numVal) || numVal < 0) {
        setError(
          `Số lượng nhập cho dòng "${line.itemName || 'vật tư'}" không hợp lệ (phải là số ≥ 0).`,
        )
        return
      }

      if (numVal > line.remainingQty) {
        setError(
          `Số lượng nhận của "${line.itemName || 'vật tư'}" (${formatVnNumber(numVal)}) vượt quá số lượng còn lại (${formatVnNumber(line.remainingQty)}).`,
        )
        return
      }

      if (numVal > 0) {
        payloadLines.push({
          po_line_id: line.id,
          po_free_line_id: line.id,
          qty: numVal,
        })
      }
    }

    if (payloadLines.length === 0) {
      setError('Vui lòng nhập số lượng nhận > 0 cho ít nhất một dòng vật tư.')
      return
    }

    try {
      setSubmitting(true)
      await onConfirm(payloadLines, note.trim())
      onClose()
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Có lỗi khi ghi nhận nhận hàng'
      const details = err?.details || err?.response?.data?.error?.details
      let materialId = details?.material_id
      if (!materialId && payloadLines.length === 1) {
        const matched = lines.find((l) => l.id === payloadLines[0].po_line_id)
        if (matched?.materialId) materialId = matched.materialId
      }
      setErrorInfo({
        message,
        materialId,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const totalOrdered = lines.reduce((acc, l) => acc + l.qty, 0)
  const totalReceived = lines.reduce((acc, l) => acc + l.receivedQty, 0)
  const totalRemaining = lines.reduce((acc, l) => acc + l.remainingQty, 0)
  const totalInput = lines.reduce((acc, l) => {
    const val = Number(inputQtys[l.id] || 0)
    return acc + (val > 0 ? val : 0)
  }, 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div
        className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-receive-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5 text-emerald-600" />
            <div>
              <span id="quick-receive-title" className="font-semibold text-slate-800 text-sm">
                Xác nhận nhận hàng — {poCode}
              </span>
              <span className="ml-2 inline-block">
                <PoStatusBadge status={poStatus} />
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={submitting}
            aria-label="Đóng"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Info & Notices */}
        <div className="mt-3 space-y-2 text-xs">
          {isDraftGroup ? (
            <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/80 p-2.5 text-blue-900">
              <Info className="h-4 w-4 shrink-0 text-blue-600 mt-0.5" />
              <div>
                Đơn hàng đang ở trạng thái <strong>Chưa gửi NCC</strong>. Khi xác nhận nhận hàng, hệ thống
                sẽ tự động hoàn tất phê duyệt và chuyển đơn sang <strong>Đang giao (SENT)</strong> trước khi
                ghi nhận hàng vào kho.
              </div>
            </div>
          ) : null}

          {targetCol === 'COMPLETED' ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 p-2.5 text-emerald-900">
              <strong>Gợi ý hoàn thành:</strong> Hệ thống đã tự động điền toàn bộ số lượng còn thiếu của từng dòng.
              Bạn có thể điều chỉnh số lượng nếu thực tế nhận khác với dự kiến.
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-2.5 text-amber-900">
              <strong>Nhận một phần:</strong> Vui lòng nhập số lượng thực tế nhận cho các dòng hàng tương ứng bên dưới.
              Các dòng để trống hoặc 0 sẽ không được ghi nhận.
            </div>
          )}

          {errorInfo ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0 text-red-600 mt-0.5" />
              <div className="space-y-1 text-left">
                <div>{errorInfo.message}</div>
                {errorInfo.materialId ? (
                  <div>
                    <a
                      href={`/purchasing/materials/${encodeURIComponent(errorInfo.materialId)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-semibold text-red-800 underline hover:text-red-950"
                    >
                      Mở vật tư để thiết lập quy đổi ↗
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Table Header actions */}
          <div className="flex items-center justify-between pt-1">
            <span className="font-medium text-slate-700">
              Danh sách vật tư chờ nhận ({lines.length} dòng):
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleFillAll}
                className="text-[11px] text-emerald-700 hover:underline font-medium"
              >
                Điền tất cả còn lại
              </button>
              <span className="text-slate-300">|</span>
              <button
                type="button"
                onClick={handleResetAll}
                className="text-[11px] text-slate-500 hover:underline font-medium"
              >
                Đặt lại về 0
              </button>
            </div>
          </div>

          {/* Lines Table */}
          <div className="max-h-60 overflow-y-auto rounded border border-slate-200">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                <tr>
                  <th className="py-2 px-2 text-center w-10">STT</th>
                  <th className="py-2 px-2">Vật tư</th>
                  <th className="py-2 px-2 text-center w-14">ĐVT</th>
                  <th className="py-2 px-2 text-right w-16">Đặt</th>
                  <th className="py-2 px-2 text-right w-16">Đã nhận</th>
                  <th className="py-2 px-2 text-right w-16 text-amber-700">Còn thiếu</th>
                  <th className="py-2 px-2 text-right w-36">Nhận đợt này</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lines.map((l, index) => (
                  <tr key={l.id} className="hover:bg-slate-50/50">
                    <td className="py-2 px-2 text-center text-slate-400">{index + 1}</td>
                    <td className="py-2 px-2">
                      <div className="font-medium text-slate-800">{l.itemName}</div>
                      <div className="text-xs text-slate-600">{Object.values(splitSpecColor(l.spec, l.spec_attributes)).filter(Boolean).join(' · ')}</div>
                    </td>
                    <td className="py-2 px-2 text-center text-slate-600">{l.uom}</td>
                    <td className="py-2 px-2 text-right font-mono text-slate-600">
                      {formatVnNumber(l.qty)}
                    </td>
                    <td className="py-2 px-2 text-right font-mono text-slate-600">
                      {formatVnNumber(l.receivedQty)}
                    </td>
                    <td className="py-2 px-2 text-right font-mono font-semibold text-amber-700">
                      {formatVnNumber(l.remainingQty)}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <input
                          type="number"
                          min={0}
                          max={l.remainingQty}
                          step="any"
                          value={inputQtys[l.id] ?? ''}
                          onChange={(e) => handleLineQtyChange(l.id, e.target.value)}
                          placeholder="0"
                          disabled={submitting}
                          className="h-7 w-20 rounded border border-slate-300 px-1.5 text-right font-mono text-xs focus:border-[var(--color-primary)] focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleFillLine(l.id, l.remainingQty)}
                          disabled={submitting}
                          title="Nhận hết phần còn lại"
                          className="rounded border border-slate-200 bg-slate-50 px-1.5 py-1 text-[10px] text-slate-600 hover:bg-slate-100"
                        >
                          Hết
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Row */}
          <div className="flex items-center justify-between rounded bg-slate-50 p-2 font-medium text-slate-700 text-xs border border-slate-200">
            <span>Tổng cộng:</span>
            <div className="flex gap-4">
              <span>
                Đặt: <strong>{formatVnNumber(totalOrdered)}</strong>
              </span>
              <span>
                Đã nhận: <strong>{formatVnNumber(totalReceived)}</strong>
              </span>
              <span>
                Còn thiếu: <strong className="text-amber-700">{formatVnNumber(totalRemaining)}</strong>
              </span>
              <span>
                Ghi nhận đợt này:{' '}
                <strong className={totalInput > 0 ? 'text-emerald-700' : 'text-slate-400'}>
                  {formatVnNumber(totalInput)}
                </strong>
              </span>
            </div>
          </div>

          {/* Note input */}
          <div className="pt-1">
            <label htmlFor={noteId} className="block font-medium text-slate-700 mb-1">
              Ghi chú phiếu nhận hàng:
            </label>
            <input
              id={noteId}
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="VD: Xe giao đợt 1 / Kiểm hàng tại xưởng..."
              disabled={submitting}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs focus:border-[var(--color-primary)] focus:outline-none"
            />
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex justify-end gap-2 pt-4 mt-3 border-t border-slate-100">
          <Button variant="secondary" size="sm" onClick={requestClose} disabled={submitting}>
            Hủy
          </Button>
          <Button variant="primary" size="sm" disabled={submitting} onClick={handleConfirm}>
            {submitting ? 'Đang ghi nhận...' : 'Xác nhận nhận hàng'}
          </Button>
        </div>
      </div>
    </div>
  )
}
